import { formatDateOnly } from './revenueItems.utils';

export const BATCH_STATUS_LABELS = {
  locked: 'Pending distribution',
  executed: 'Executed',
  cancelled: 'Cancelled',
};

/** Legacy DB rows may still have status `draft`; treat like locked in UI. */
export const isPendingDistributionBatch = (status) => status === 'locked' || status === 'draft';

const ETH_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/i;

/** True if string is a valid EVM address (0x + 40 hex). */
export const isValidEthAddress = (value) => {
  if (!value || typeof value !== 'string') return false;
  return ETH_ADDRESS_RE.test(value.trim());
};

/**
 * Parse LP holder CSV: first row is header (e.g. "address"), following rows are 0x addresses.
 * One address per line, or first column when comma-separated.
 */
export const parseLpHolderCsvText = (text) => {
  if (!text || typeof text !== 'string') {
    return { addresses: [], error: 'File is empty' };
  }

  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { addresses: [], error: 'CSV must have a header row and at least one address' };
  }

  const addresses = [];

  for (let i = 1; i < lines.length; i += 1) {
    const cell = lines[i].split(',')[0].replace(/^"|"$/g, '').trim();
    if (isValidEthAddress(cell)) {
      addresses.push(cell.toLowerCase());
    }
  }

  if (!addresses.length) {
    return { addresses: [], error: 'No valid Ethereum addresses found in CSV' };
  }

  return { addresses: [...new Set(addresses)], error: null };
};

export const formatBatchPeriod = (start, end) => {
  if (!start && !end) return '—';
  const fmt = (d) => {
    if (!d) return '?';
    const part = formatDateOnly(d);
    return part === '-' ? '?' : part;
  };
  return `${fmt(start)} → ${fmt(end)}`;
};

export const formatRecipientTypeLabel = (type) => {
  if (type === 'lp_holder') return 'LP holder';
  if (type === 'order_handler') return 'Order handler';
  if (type === 'microjob') return 'Microjob';
  if (type === 'treasury') return 'DAO treasury';
  if (type === 'platform') return 'Platform';
  return type || '—';
};

export const mapRecipientType = (address, daoTreasury, platformTreasury) => {
  const a = address?.toLowerCase();
  if (a === daoTreasury?.toLowerCase()) return 'treasury';
  if (a === platformTreasury?.toLowerCase()) return 'platform';
  return 'lp_holder';
};

const RECIPIENT_TYPE_ORDER = { treasury: 0, platform: 1, lp_holder: 2 };

/** DAO treasury → platform → LP holders (LP rows by amount descending). */
export const sortPayoutDistributions = (distributions) => {
  if (!distributions?.length) return [];
  return [...distributions].sort((a, b) => {
    const orderA = RECIPIENT_TYPE_ORDER[a.recipientType] ?? 99;
    const orderB = RECIPIENT_TYPE_ORDER[b.recipientType] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return Number(b.amount) - Number(a.amount);
  });
};

/** Aligns with ServiceRevenueSharingTab / antd message.error text extraction. */
export const formatRevenueSharingErrorMessage = (err, fallback = 'Something went wrong') => {
  if (!err) return fallback;

  let errorMessage =
    err.reason ||
    err.error?.reason ||
    err.error?.message ||
    err.response?.data?.error?.message ||
    err.response?.data?.message ||
    err.data?.message ||
    err.message;

  if (!errorMessage || typeof errorMessage !== 'string') {
    return fallback;
  }
  return errorMessage;
};

export const isTxUserRejection = (err) =>
  err?.code === 4001 || err?.code === 'ACTION_REJECTED';
